import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './AdmissionsPage.css';
import admissionsBg from '../../assets/images/admissions.svg';
import schedulebg from '../../assets/images/schedule.svg';
import viewIcon from '../../assets/images/upload2.svg';
import peopleIcon from '../../assets/images/team3.svg';
import missionIcon from '../../assets/images/confirmation2.svg';
import calendor from '../../assets/images/calendor.svg';
import google from '../../assets/images/google.svg';
import finalVid from '../../assets/images/final vid.mp4';
import mrsFosterVid from '../../assets/images/Mrs Foster Final.mp4';
import welcomeimage from '../../assets/images/welcomeimage.jpg';
import logoMark from '../../assets/images/logo.svg';
import { slotBookingsPublicApi } from '../../api/adminApi';
import { TESTIMONIALS } from '../../constants/testimonials';

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MORNING_SLOTS = ['8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM'];
const AFTERNOON_SLOTS = ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM'];
const ALL_SLOTS = [...MORNING_SLOTS, ...AFTERNOON_SLOTS];

/** Converts ISO date string to display format (e.g. "9:00 AM") to match our slot labels. */
function isoToSlotLabel(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes === 0 ? '00' : String(minutes).padStart(2, '0');
  const label = `${displayHours}:${displayMinutes} ${period}`;
  return ALL_SLOTS.includes(label) ? label : null;
}

/** Parses slot label (e.g. "9:00 AM") to hours and minutes. */
function parseSlotLabel(label) {
  const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (match[3].toUpperCase() === 'PM' && hours !== 12) hours += 12;
  if (match[3].toUpperCase() === 'AM' && hours === 12) hours = 0;
  return { hours, minutes };
}

/** Builds slot_start and slot_end ISO strings from date + slot label (30-min slot). */
function slotToISO(date, slotLabel) {
  const parsed = parseSlotLabel(slotLabel);
  if (!parsed) return null;
  const start = new Date(date);
  start.setHours(parsed.hours, parsed.minutes, 0, 0);
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 30);
  return { slot_start: start.toISOString(), slot_end: end.toISOString() };
}

/** Returns true if the slot time has already passed on the given date (for today). */
function isSlotInPast(slotLabel, date) {
  if (!date) return false;
  const today = new Date();
  if (date.getDate() !== today.getDate() || date.getMonth() !== today.getMonth() || date.getFullYear() !== today.getFullYear()) {
    return false;
  }
  const parsed = parseSlotLabel(slotLabel);
  if (!parsed) return false;
  const slotTime = new Date(today);
  slotTime.setHours(parsed.hours, parsed.minutes, 0, 0);
  return today >= slotTime;
}

/** Returns 7 days starting from today + weekOffset*7 (no past days). */
function getWeekDates(weekOffset = 0) {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** Validation helpers for the Your Information form. */
const FORM_VALIDATORS = {
  parentName: (v) => {
    const t = (v || '').trim();
    if (!t) return 'Parent name is required';
    if (t.length < 2) return 'Please enter at least 2 characters';
    return null;
  },
  childName: (v) => {
    const t = (v || '').trim();
    if (!t) return "Child's name is required";
    if (t.length < 2) return 'Please enter at least 2 characters';
    return null;
  },
  childAge: (v) => {
    const t = (v || '').trim();
    if (!t) return "Child's age is required";
    const n = parseInt(t, 10);
    if (Number.isNaN(n) || n < 1 || n > 12) return 'Please enter an age between 1 and 12';
    return null;
  },
  phone: (v) => {
    const t = (v || '').trim();
    if (!t) return 'Phone number is required';
    const digits = t.replace(/\D/g, '');
    if (digits.length < 10) return 'Please enter a valid phone number';
    return null;
  },
  email: (v) => {
    const t = (v || '').trim();
    if (!t) return 'Email is required';
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(t)) return 'Please enter a valid email address';
    return null;
  },
  message: (v) => {
    const t = (v || '').trim();
    if (!t) return 'Message is required';
    return null;
  },
};

function validateForm(data) {
  const errors = {};
  (Object.keys(FORM_VALIDATORS)).forEach((key) => {
    const err = FORM_VALIDATORS[key](data[key]);
    if (err) errors[key] = err;
  });
  return errors;
}

const STEPS = [
  {
    number: '01',
    title: 'Schedule a Tour',
    description:
      "Visit our campus to meet our teachers, see our classrooms, and learn about our programs firsthand.",
    icon: schedulebg,
  },
  {
    number: '02',
    title: 'Submit Application',
    description:
      "Complete our online application form with your child's information and preferred program.",
    icon: viewIcon,
  },
  {
    number: '03',
    title: 'Meet Our Team',
    description:
      "Attend a family interview to discuss your child's needs and ensure we're the right fit.",
    icon: peopleIcon,
  },
  {
    number: '04',
    title: 'Confirm Enrollment',
    description:
      "Receive your acceptance letter and complete enrollment forms to secure your child's spot.",
    icon: missionIcon,
  },
];

function AdmissionsPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlotLabels, setBookedSlotLabels] = useState(new Set());
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [formData, setFormData] = useState({
    parentName: '',
    childName: '',
    childAge: '',
    phone: '',
    email: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showFormSection, setShowFormSection] = useState(false);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const [playingTestimonialIndex, setPlayingTestimonialIndex] = useState(null);
  const testimonialVideoRefs = React.useRef([]);
  const [testimonialModalIndex, setTestimonialModalIndex] = React.useState(null);
  const suppressTestimonialClickRef = React.useRef(false);
  const testimonialVideos = React.useMemo(
    () => [
      { src: finalVid, label: 'Parent testimonial video 1' },
      { src: mrsFosterVid, label: 'Parent testimonial video 2' },
    ],
    []
  );

  const goToTestimonial = useCallback(
    (nextIndex) => {
      const total = TESTIMONIALS.length;
      setActiveTestimonialIndex(((nextIndex % total) + total) % total);
    },
    []
  );

  const goPrevTestimonial = useCallback(
    () => goToTestimonial(activeTestimonialIndex - 1),
    [activeTestimonialIndex, goToTestimonial]
  );

  const goNextTestimonial = useCallback(
    () => goToTestimonial(activeTestimonialIndex + 1),
    [activeTestimonialIndex, goToTestimonial]
  );

  // Pause any testimonial video when the slide changes.
  useEffect(() => {
    testimonialVideoRefs.current.forEach((v) => {
      try {
        if (v && !v.paused) v.pause();
      } catch {
        // no-op
      }
    });
    setPlayingTestimonialIndex(null);
  }, [activeTestimonialIndex]);

  const openTestimonialModal = React.useCallback((idx) => {
    const v = testimonialVideoRefs.current[idx];
    try {
      if (v && !v.paused) v.pause();
    } catch {
      // no-op
    }
    setPlayingTestimonialIndex(null);
    setTestimonialModalIndex(idx);
  }, []);

  const closeTestimonialModal = React.useCallback(() => {
    setTestimonialModalIndex(null);
  }, []);

  const toggleTestimonialVideo = React.useCallback((idx) => {
    const video = testimonialVideoRefs.current[idx];
    if (!video) return;

    if (video.paused) {
      testimonialVideoRefs.current.forEach((v, i) => {
        if (v && i !== idx && !v.paused) v.pause();
      });
      video
        .play()
        .then(() => setPlayingTestimonialIndex(idx))
        .catch(() => setPlayingTestimonialIndex(idx));
    } else {
      video.pause();
      setPlayingTestimonialIndex(null);
    }
  }, []);

  const weekDates = getWeekDates(weekOffset);

  // Scroll to booking section when navigating with #schedule-tour hash
  useEffect(() => {
    let timer;
    const handleHash = () => {
      if (window.location.hash === '#schedule-tour') {
        setShowFormSection(false);
        timer = setTimeout(() => {
          document.getElementById('schedule-tour')?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => {
      window.removeEventListener('hashchange', handleHash);
      clearTimeout(timer);
    };
  }, []);

  // Scroll to form section when user clicks Continue
  useEffect(() => {
    if (showFormSection) {
      document.getElementById('your-information')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showFormSection]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleFieldBlur = (e) => {
    const { name } = e.target;
    const err = FORM_VALIDATORS[name]?.(formData[name]);
    setFormErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  // Clear selected time if it has passed (e.g. user kept page open and time advanced)
  useEffect(() => {
    if (!selectedTime || !selectedDate) return;
    if (isSlotInPast(selectedTime, selectedDate)) {
      setSelectedTime(null);
    }
  }, [selectedTime, selectedDate]);

  // Re-check past slots every minute while on today's date (handles time advancing)
  useEffect(() => {
    if (!selectedDate) return;
    const today = new Date();
    const isToday =
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear();
    if (!isToday) return;
    const interval = setInterval(() => {
      setSelectedTime((prev) => (prev && isSlotInPast(prev, selectedDate) ? null : prev));
    }, 60_000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const handleDateClick = useCallback(async (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setBookedSlotLabels(new Set());
    setAvailabilityError(null);
    setAvailabilityLoading(true);
    try {
      const { booked_slots = [] } = await slotBookingsPublicApi.checkAvailability(date);
      const booked = new Set();
      booked_slots.forEach(({ slot_start, slot_end }) => {
        const label = isoToSlotLabel(slot_start) ?? isoToSlotLabel(slot_end);
        if (label) booked.add(label);
      });
      setBookedSlotLabels(booked);
    } catch (err) {
      setAvailabilityError(err?.userMessage || err?.message || 'Failed to load availability');
      setBookedSlotLabels(new Set());
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!selectedDate || !selectedTime) {
      setSubmitError('Please select a date and time for your tour.');
      return;
    }

    const slot = slotToISO(selectedDate, selectedTime);
    if (!slot) {
      setSubmitError('Invalid time slot. Please try again.');
      return;
    }

    setFormErrors({});
    setSubmitLoading(true);
    try {
      await slotBookingsPublicApi.create({
        slot_start: slot.slot_start,
        slot_end: slot.slot_end,
        parent_name: formData.parentName.trim(),
        child_name: formData.childName.trim(),
        child_age: parseInt(formData.childAge, 10) || 0,
        phone_number: formData.phone.trim(),
        email: formData.email.trim(),
        message: formData.message.trim() || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setSubmitSuccess(true);
      setFormData({ parentName: '', childName: '', childAge: '', phone: '', email: '', message: '' });
      setSelectedDate(null);
      setSelectedTime(null);
      setBookedSlotLabels(new Set());
      setShowFormSection(false);
    } catch (err) {
      setSubmitError(err?.userMessage || err?.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  const displayMonth = weekDates[0]?.toLocaleString('default', { month: 'long' }) ?? '';

  return (
    <main className="admissions-page">
      {/* Hero Section */}
      <section className="admissions-hero" aria-label="Enrollment journey">
        <div className="admissions-hero-bg" style={{ backgroundImage: `url(${admissionsBg})` }} aria-hidden="true" />
        {/* <div className="admissions-hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFD84D" d="M0 80V40c120 20 240 40 360 40s240-20 360-40 240-40 360-40 240 20 360 40v40H0z" />
          </svg>
        </div> */}
        <div className="admissions-hero-inner">
          <h1 className="admissions-hero-title">Start Your Enrollment Journey</h1>
          <p className="admissions-hero-desc">
            Joining The David Foster Preschool Academy is simple. Follow our easy enrollment process to give your child a bright beginning.
          </p>
        </div>
      </section>

      {/* Enrollment Process */}
      <section className="admissions-process" aria-label="Enrollment process">
        <div className="admissions-process-inner">
          <span className="admissions-process-label">Enrollment Process</span>
          <h2 className="admissions-process-title">Four Simple Steps</h2>
          <div className="admissions-steps">
            {STEPS.map((step, idx) => (
              <article key={idx} className="admissions-step-card">
                <span className="admissions-step-number" aria-hidden="true">
                  {step.number}
                </span>
                <span className="admissions-step-icon" aria-hidden="true">
                  <img src={step.icon} alt="" />
                </span>
                <h3 className="admissions-step-title">{step.title}</h3>
                <p className="admissions-step-desc">{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Select Date and Time */}
      {!showFormSection && (
      <section className="admissions-schedule" id="schedule-tour" aria-label="Schedule tour">
        <div className="admissions-schedule-inner">
          <h2 className="admissions-schedule-title">Select Date and Time</h2>
          <div className="admissions-schedule-card">
            {/* Week navigation */}
            <div className="admissions-schedule-nav">
              <button
                type="button"
                className="admissions-schedule-nav-btn"
                onClick={() => setWeekOffset((o) => o - 1)}
                disabled={weekOffset <= 0}
                aria-label="Previous week"
              >
                &lt;
              </button>
              <span className="admissions-schedule-nav-label">
                <span className="admissions-schedule-nav-month">{displayMonth}</span>
                <span className="admissions-schedule-nav-week">
                  {weekOffset === 0 ? 'This week' : weekOffset === 1 ? 'Next week' : `+${weekOffset} weeks`}
                </span>
              </span>
              <button
                type="button"
                className="admissions-schedule-nav-btn"
                onClick={() => setWeekOffset((o) => o + 1)}
                aria-label="Next week"
              >
                &gt;
              </button>
            </div>

            {/* Date cards */}
            <div className="admissions-schedule-dates">
              {weekDates.map((d) => {
                const isSelected =
                  selectedDate &&
                  d.getDate() === selectedDate.getDate() &&
                  d.getMonth() === selectedDate.getMonth() &&
                  d.getFullYear() === selectedDate.getFullYear();
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    className={`admissions-schedule-date-card ${isSelected ? 'admissions-schedule-date-card--selected' : ''}`}
                    onClick={() => handleDateClick(d)}
                  >
                    <span className="admissions-schedule-date-day">{DAYS[d.getDay()]}</span>
                    <span className="admissions-schedule-date-num">{d.getDate()}</span>
                  </button>
                );
              })}
            </div>

            {/* Availability loading/error */}
            {availabilityLoading && (
              <p className="admissions-schedule-status admissions-schedule-status--loading">
                Checking availability…
              </p>
            )}
            {availabilityError && !availabilityLoading && (
              <p className="admissions-schedule-status admissions-schedule-status--error" role="alert">
                {availabilityError}
              </p>
            )}

            {/* Current day display */}
            <p className="admissions-schedule-current">
              {selectedDate
                ? selectedDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : weekDates[0]?.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }) ?? ''}
            </p>

            {/* Time slots */}
            <div className="admissions-schedule-times">
              <div className="admissions-schedule-time-group">
                <span className="admissions-schedule-time-label">Morning</span>
                <div className="admissions-schedule-time-slots">
                  {MORNING_SLOTS.map((slot) => {
                    const isSelected = selectedTime === slot;
                    const isBooked = bookedSlotLabels.has(slot);
                    const isPast = isSlotInPast(slot, selectedDate);
                    const isDisabled = !selectedDate || availabilityLoading || isBooked || isPast;
                    const title = isPast
                      ? 'This time slot has already passed'
                      : isBooked
                        ? 'This slot is already booked'
                        : !selectedDate
                          ? 'Select a date first'
                          : undefined;
                    return (
                      <button
                        key={slot}
                        type="button"
                        className={`admissions-schedule-time-btn ${isSelected ? 'admissions-schedule-time-btn--selected' : ''} ${isDisabled ? 'admissions-schedule-time-btn--disabled' : ''}`}
                        onClick={() => !isDisabled && setSelectedTime(slot)}
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                        title={title}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="admissions-schedule-time-group">
                <span className="admissions-schedule-time-label">Afternoon</span>
                <div className="admissions-schedule-time-slots">
                  {AFTERNOON_SLOTS.map((slot) => {
                    const isSelected = selectedTime === slot;
                    const isBooked = bookedSlotLabels.has(slot);
                    const isPast = isSlotInPast(slot, selectedDate);
                    const isDisabled = !selectedDate || availabilityLoading || isBooked || isPast;
                    const title = isPast
                      ? 'This time slot has already passed'
                      : isBooked
                        ? 'This slot is already booked'
                        : !selectedDate
                          ? 'Select a date first'
                          : undefined;
                    return (
                      <button
                        key={slot}
                        type="button"
                        className={`admissions-schedule-time-btn ${isSelected ? 'admissions-schedule-time-btn--selected' : ''} ${isDisabled ? 'admissions-schedule-time-btn--disabled' : ''}`}
                        onClick={() => !isDisabled && setSelectedTime(slot)}
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                        title={title}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="admissions-form-submit admissions-schedule-continue"
              onClick={() => setShowFormSection(true)}
              disabled={!selectedDate || !selectedTime}
            >
              Continue
            </button>
          </div>
        </div>
      </section>
      )}

      {/* Your Information Form */}
      {showFormSection && (
      <section className="admissions-form-section" id="your-information" aria-label="Your information">
        <div className="admissions-form-inner">
          <h2 className="admissions-form-title">Your Information</h2>
          <button
            type="button"
            className="admissions-form-back"
            onClick={() => setShowFormSection(false)}
            aria-label="Back to date and time selection"
          >
            ← Change date & time
          </button>
          <form className="admissions-form-card" onSubmit={handleSubmit}>
            <div className="admissions-form-row">
              <div className={`admissions-form-field ${formErrors.parentName ? 'admissions-form-field--error' : ''}`}>
                <label htmlFor="parentName">Parent Name *</label>
                <input
                  id="parentName"
                  name="parentName"
                  type="text"
                  value={formData.parentName}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  required
                  aria-invalid={!!formErrors.parentName}
                  aria-describedby={formErrors.parentName ? 'parentName-error' : undefined}
                />
                {formErrors.parentName && (
                  <span id="parentName-error" className="admissions-form-error" role="alert">
                    {formErrors.parentName}
                  </span>
                )}
              </div>
              <div className={`admissions-form-field ${formErrors.childName ? 'admissions-form-field--error' : ''}`}>
                <label htmlFor="childName">Child&apos;s Name *</label>
                <input
                  id="childName"
                  name="childName"
                  type="text"
                  value={formData.childName}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  required
                  aria-invalid={!!formErrors.childName}
                  aria-describedby={formErrors.childName ? 'childName-error' : undefined}
                />
                {formErrors.childName && (
                  <span id="childName-error" className="admissions-form-error" role="alert">
                    {formErrors.childName}
                  </span>
                )}
              </div>
            </div>
            <div className="admissions-form-row">
              <div className={`admissions-form-field ${formErrors.childAge ? 'admissions-form-field--error' : ''}`}>
                <label htmlFor="childAge">Child&apos;s Age *</label>
                <input
                  id="childAge"
                  name="childAge"
                  type="text"
                  inputMode="numeric"
                  value={formData.childAge}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  required
                  aria-invalid={!!formErrors.childAge}
                  aria-describedby={formErrors.childAge ? 'childAge-error' : undefined}
                />
                {formErrors.childAge && (
                  <span id="childAge-error" className="admissions-form-error" role="alert">
                    {formErrors.childAge}
                  </span>
                )}
              </div>
              <div className={`admissions-form-field ${formErrors.phone ? 'admissions-form-field--error' : ''}`}>
                <label htmlFor="phone">Phone *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  required
                  aria-invalid={!!formErrors.phone}
                  aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                />
                {formErrors.phone && (
                  <span id="phone-error" className="admissions-form-error" role="alert">
                    {formErrors.phone}
                  </span>
                )}
              </div>
            </div>
            <div className={`admissions-form-field ${formErrors.email ? 'admissions-form-field--error' : ''}`}>
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                onBlur={handleFieldBlur}
                required
                aria-invalid={!!formErrors.email}
                aria-describedby={formErrors.email ? 'email-error' : undefined}
              />
              {formErrors.email && (
                <span id="email-error" className="admissions-form-error" role="alert">
                  {formErrors.email}
                </span>
              )}
            </div>
            <div className={`admissions-form-field ${formErrors.message ? 'admissions-form-field--error' : ''}`}>
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleFormChange}
                onBlur={handleFieldBlur}
                required
                aria-invalid={!!formErrors.message}
                aria-describedby={formErrors.message ? 'message-error' : undefined}
              />
              {formErrors.message && (
                <span id="message-error" className="admissions-form-error" role="alert">
                  {formErrors.message}
                </span>
              )}
            </div>

            {submitSuccess && (
              <p className="admissions-form-status admissions-form-status--success" role="status">
                Thank you! Your tour inquiry has been submitted. We&apos;ll be in touch soon.
              </p>
            )}
            {submitError && (
              <p className="admissions-form-status admissions-form-status--error" role="alert">
                {submitError}
              </p>
            )}

            <button
              type="submit"
              className="admissions-form-submit"
              disabled={submitLoading}
            >
              {submitLoading ? 'Submitting…' : 'Submit Inquiry'}
            </button>
          </form>
        </div>
      </section>
      )}
            {/* Testimonials */}
            <section className="testimonials" aria-label="Testimonials carousel">
        <div className="testimonials-inner">
          <div className="testimonials-header">
            <div>
              <p className="section-eyebrow">Testimonials</p>
              <h2 className="section-title">Loved by Families</h2>
              <p className="section-desc">Don&apos;t just take our word for it—hear from the families we serve.</p>
            </div>
            <div className="testimonials-google">
              <div className="testimonials-google-top" aria-hidden="true">
                <span className="testimonials-google-mark"><img src={google} alt=""/></span>
                <div className="testimonials-stars">
                  <p className="testimonials-stars-rating">★★★★★</p>
                <p>Based on 500+ reviews</p>
                </div>
              </div>
             
            </div>
          </div>

          <div className="testimonial-carousel">
            <button
              type="button"
              className="testimonial-arrow testimonial-arrow-prev"
              aria-label="Previous testimonial"
              onClick={goPrevTestimonial}
            >
              ‹
            </button>
            <button
              type="button"
              className="testimonial-arrow testimonial-arrow-next"
              aria-label="Next testimonial"
              onClick={goNextTestimonial}
            >
              ›
            </button>

            <div
              className="testimonial-cards-track"
              style={{
                transform: `translateX(-${activeTestimonialIndex * (100 / TESTIMONIALS.length)}%)`,
                width: `${TESTIMONIALS.length * 100}%`,
              }}
            >
              {TESTIMONIALS.map((t, idx) => (
                <article
                  key={t.id}
                  className="testimonial-card"
                  style={{ flexBasis: `${100 / TESTIMONIALS.length}%` }}
                  aria-hidden={idx !== activeTestimonialIndex}
                >
                  <div className="testimonial-media">
                    {playingTestimonialIndex !== idx && (
                      <div className="testimonial-poster" aria-hidden="true">
                        <img className="testimonial-poster-logo" src={logoMark} alt="" />
                      </div>
                    )}

                    {playingTestimonialIndex !== idx && (
                      <button
                        type="button"
                        className="testimonial-play-button"
                        aria-label="Play testimonial video"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTestimonialModal(idx);
                        }}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M8 5v14l11-7z" fill="currentColor" />
                        </svg>
                      </button>
                    )}

                    <video
                      className={`testimonial-video ${playingTestimonialIndex === idx ? 'is-playing' : ''}`}
                      ref={(el) => {
                        testimonialVideoRefs.current[idx] = el;
                      }}
                      preload="metadata"
                      aria-label={testimonialVideos[idx % testimonialVideos.length].label}
                      playsInline
                      onPlay={() => setPlayingTestimonialIndex(idx)}
                      onPause={() =>
                        setPlayingTestimonialIndex((cur) => {
                          if (cur === idx) return null;
                          return cur;
                        })
                      }
                      onClick={() => {
                        if (suppressTestimonialClickRef.current) return;
                        toggleTestimonialVideo(idx);
                      }}
                      onDoubleClick={() => {
                        suppressTestimonialClickRef.current = true;
                        window.setTimeout(() => {
                          suppressTestimonialClickRef.current = false;
                        }, 250);
                        openTestimonialModal(idx);
                      }}
                    >
                      <source src={testimonialVideos[idx % testimonialVideos.length].src} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="testimonial-body">
                    <p className="testimonial-quote">&quot;{t.quote}&quot;</p>
                    <div className="testimonial-name">
                      {t.name} <span className="testimonial-verified" aria-hidden="true">●</span>
                    </div>
                    <div className="testimonial-time">{t.time}</div>
                    <div className="testimonial-stars" aria-hidden="true">★★★★★</div>
                    <div className="testimonial-author">{t.role || 'Parent'}</div>
                  </div>
                </article>
              ))}
            </div>

            <div className="testimonials-dots" role="tablist" aria-label="Testimonial slides">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`dot ${idx === activeTestimonialIndex ? 'active' : ''}`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                  aria-selected={idx === activeTestimonialIndex}
                  onClick={() => goToTestimonial(idx)}
                />
              ))}
            </div>
          </div>

          <div className="testimonials-join" aria-hidden="true">
            <div className="testimonials-join-avatars">
              <span className="join-avatar">J</span>
              <span className="join-avatar">M</span>
              <span className="join-avatar">S</span>
              <span className="join-avatar">+</span>
            </div>
            <div className="testimonials-join-text">Join 500+ happy families</div>
          </div>
        </div>
      </section>

      {testimonialModalIndex != null && (
        <div
          className="testimonial-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Testimonial video"
          onClick={closeTestimonialModal}
        >
          <div className="testimonial-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="testimonial-modal-close"
              aria-label="Close video"
              onClick={closeTestimonialModal}
            >
              ×
            </button>
            <video
              key={`testimonial-modal-video-${testimonialModalIndex}`}
              className="testimonial-modal-video"
              controls
              autoPlay
              playsInline
            >
              <source
                src={testimonialVideos[testimonialModalIndex % testimonialVideos.length].src}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

           {/* CTA */}
           <section className="cta" id="apply-cta">
        <div className="cta-inner">
          <h2 className="cta-title">Ready to Apply? </h2>
          <p className="cta-desc">Start your application today or schedule a tour to learn more about our programs.</p>
          <div className="cta-actions">
            <a href="#schedule-tour" className="btn btn-primary cta-primary">
              <span className="btn-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                </svg>
              </span>
              Schedule Tour
            </a>
            <Link to="/contact" className="btn btn-outline cta-secondary">
              Inquiry Now
            </Link>
          </div>
{/* 
          <div className="cta-meta" aria-label="Contact and hours">
            <a className="cta-meta-item" href="tel:718-462-0776">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 3.5L9.2 7.2C9.4 7.7 9.3 8.3 8.9 8.7L7.6 10C9 12.7 11.3 15 14 16.4L15.3 15.1C15.7 14.7 16.3 14.6 16.8 14.8L20.5 16.5C21.1 16.8 21.3 17.4 21.1 18C20.6 19.5 19.2 20.5 17.6 20.5C10.1 20.5 3.5 13.9 3.5 6.4C3.5 4.8 4.5 3.4 6 2.9C6.6 2.7 7.2 2.9 7.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">718-462-0776</span>
            </a>

            <div className="cta-meta-item">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12L14.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">Monday - Friday: 8:00 AM - 7:00 PM</span>
            </div>
          </div> */}
        </div>
      </section>

    </main>
  );
}

export default AdmissionsPage;
